import React from 'react';
import FolderCard from '../FolderCard/FolderCard';
import { ReactComponent as ImagePlaceholder } from '../../images/image-placeholder.svg';
import { getStatusLabel, getStatusColor } from '../../utils/statusHelpers';
import styles from './ProjectItemCard.module.scss';

interface ProjectItemCardProps {
  item: any;
  isSelected: boolean;
  onSelectChange: (checked: boolean) => void;
  onNavigate: (item: any) => void;
  formatDate: (dateString: string) => string;
  selectable?: boolean;
}

const ProjectItemCard: React.FC<ProjectItemCardProps> = ({
  item,
  isSelected,
  onSelectChange,
  onNavigate,
  formatDate,
  selectable = true
}) => {
  // En yeni versiyonu bul
  const getLatestVersion = () => {
    if (!item.versions || !Array.isArray(item.versions) || item.versions.length === 0) {
      return null;
    }
    
    return item.versions.reduce((latest: any, current: any) => {
      const latestDate = new Date(latest.createdAt);
      const currentDate = new Date(current.createdAt);
      return currentDate > latestDate ? current : latest;
    });
  };

  const latestVersion = getLatestVersion();

  const renderFilePreview = () => {
    if (item.entryType !== 'File') {
      return item.thumbnailUrl ? 
        <img src={item.thumbnailUrl} alt={item.name} /> : 
        <ImagePlaceholder style={{ width: "100%", height: "100%" }} />;
    }

    // File preview logic
    const thumbnailUrl = item.thumbnailUrl;
    const fileUrl = item.fileUrl || item.versions?.[0]?.fileUrl;
    
    // Eğer thumbnail varsa, direkt göster
    if (thumbnailUrl) {
      return <img src={thumbnailUrl} alt={item.name} className={styles.previewImage} />;
    }
    
    // Thumbnail yoksa, file URL'den preview oluştur
    if (fileUrl) {
      // URL'den uzantıyı al
      let extension = '';
      const urlParts = fileUrl.split('/');
      const fileNameFromUrl = urlParts[urlParts.length - 1];
      if (fileNameFromUrl) {
        const fileNameParts = fileNameFromUrl.split('.');
        if (fileNameParts.length > 1) {
          extension = fileNameParts[fileNameParts.length - 1]?.split('?')[0]?.toLowerCase();
        }
      }
      
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension)) {
        return <img src={fileUrl} alt={item.name} className={styles.previewImage} />;
      } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        return (
          <video 
            src={fileUrl} 
            className={styles.previewVideo}
            muted
            onLoadedData={(e) => {
              // Video thumbnail oluştur
              const video = e.target as HTMLVideoElement;
              video.currentTime = 1;
            }}
          />
        );
      }
    }
    
    // Hiçbiri yoksa fallback
    return <ImagePlaceholder style={{ width: "100%", height: "100%" }} />;
  };

  return (
    <FolderCard
      key={item.id}
      selectable={selectable}
      selected={isSelected}
      onSelectChange={onSelectChange}
      folderType={item.entryType === 'File' ? 'file' : 'folder'}
      style={{ margin: 0, cursor: 'pointer' }}
      onClick={() => onNavigate(item)}
      sharedUsers={item.share ? item.share.map((shareItem: any) => ({
        id: shareItem.id || Math.random(),
        firstName: shareItem.firstName || shareItem.email?.split('@')[0] || 'User',
        lastName: shareItem.lastName || '',
        thumbnailUrl: shareItem.thumbnailUrl,
        organizationName: shareItem.organizationName
      })) : []}

    >
      <div className={styles.gradientBorderBox}>
        <div className={styles.gradientBorderBoxContent}>
          {item.name}
        </div>
      </div>
      
      <div className={styles.projectImage}>
        {renderFilePreview()}
      </div>
      
      <div className={styles.projectDetailInfo}>
        {/* File entry type için status göster, Project ve Folder için gösterme */}
        
        
        {/* Share Avatar'ları */}
        {item.share && item.share.length > 0 && (
          <div className={styles.shareAvatarsContainer}>
            <div className={styles.shareAvatarsWrapper}>
              {item.share.slice(0, 3).map((shareItem: any, idx: number) => (
                <div
                  key={shareItem.id || idx}
                  className={styles.shareAvatar}
                  style={{
                    backgroundColor: shareItem.thumbnailUrl ? 'transparent' : "var(--app-accent-color)",
                    marginLeft: idx > 0 ? -8 : 0,
                    zIndex: 3 - idx,
                  }}
                >
                  {shareItem.thumbnailUrl ? (
                    <img 
                      src={shareItem.thumbnailUrl} 
                      alt={`${shareItem.firstName} ${shareItem.lastName}`}
                      style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                    />
                  ) : (
                    <span style={{ 
                      color: '#fff', 
                      fontWeight: 700, 
                      fontSize: 14 
                    }}>
                      {(shareItem.firstName?.[0] || shareItem.email?.[0] || 'U')?.toUpperCase()}
                    </span>
                  )}
                </div>
              ))}
              {item.share.length > 3 && (
                <div className={styles.shareAvatarMore}>
                  +{item.share.length - 3}
                </div>
              )}
            </div>
          </div>
        )}
        {item.entryType === 'File' && (
          <div className={styles.projectStatusContainer}>
            <div className={styles.projectStatus} style={{ 
              backgroundColor: getStatusColor(item.versions?.[0]?.status || item.status)
            }}>
              {getStatusLabel(item.versions?.[0]?.status || item.status)}
            </div>
          </div>
        )}
      </div>
      
      <div className={styles.projectFooter}>
        <div className={`${styles.gradientBorderTop} ${item.entryType === 'File' && item.versions && Array.isArray(item.versions) && item.versions.length > 1 ? styles.withVersions : styles.withoutVersions}`}>
          {/* Sol taraf: Versiyon bilgileri gruplu - sadece 2+ versiyon varsa göster */}
          {item.entryType === 'File' && item.versions && Array.isArray(item.versions) && item.versions.length > 1 && (
            <div className={styles.leftSection}>
              <span className={styles.versionCount}>
                {item.versions.length} version
              </span>
              {latestVersion && (
                <span className={styles.versionBadge}>
                  {latestVersion.name}
                </span>
              )}
            </div>
          )}
          
          {/* Sağ: Tarih - her zaman göster */}
          <div className={styles.rightSection}>
            <span className={styles.date}>{item.updatedAt ? formatDate(item.updatedAt) : formatDate(item.createdAt)}</span>
          </div>
        </div>
      </div>
    </FolderCard>
  );
};

export default ProjectItemCard; 